import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import humanTime from 'flarum/common/helpers/humanTime';
import extractText from 'flarum/common/utils/extractText';
import ItemList from 'flarum/common/utils/ItemList';
import AddPasskeyModal from './AddPasskeyModal';
import RenamePasskeyModal from './RenamePasskeyModal';
import { isSupported, bulkRevoke } from '../lib/webauthn';
export default class PasskeyList extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "loaded", false);
    _defineProperty(this, "loading", {});
    _defineProperty(this, "bulkLoading", false);
    _defineProperty(this, "passkeys", []);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.refresh();
  }
  view() {
    if (!this.loaded) {
      return m(LoadingIndicator, null);
    }
    return m("div", {
      className: "PasskeyList"
    }, this.passkeys.length === 0 ? m("div", {
      className: "PasskeyList--empty"
    }, app.translator.trans('datlechin-passkey.forum.settings.list.empty')) : this.passkeys.map(p => this.itemView(p)), m("div", {
      className: "PasskeyList-actions"
    }, m(Button, {
      className: "Button",
      icon: "fas fa-plus",
      disabled: !isSupported(),
      onclick: this.openAdd.bind(this)
    }, app.translator.trans('datlechin-passkey.forum.settings.add_button')), this.passkeys.length > 0 && m(Button, {
      className: "Button Button--link",
      loading: this.bulkLoading,
      onclick: this.bulkRevoke.bind(this)
    }, app.translator.trans('datlechin-passkey.forum.settings.bulk_revoke_button'))));
  }
  itemView(passkey) {
    return m("div", {
      className: "PasskeyList-item",
      key: passkey.id()
    }, this.itemContents(passkey).toArray());
  }
  itemContents(passkey) {
    const items = new ItemList();
    items.add('icon', m("div", {
      className: "PasskeyList-item-icon"
    }, m(Icon, {
      name: "fas fa-key"
    })), 50);
    items.add('info', m("div", {
      className: "PasskeyList-item-info"
    }, this.itemInfo(passkey).toArray()), 40);
    items.add('actions', m("div", {
      className: "PasskeyList-item-actions"
    }, this.itemActions(passkey).toArray()), 30);
    return items;
  }
  itemInfo(passkey) {
    var _passkey$authenticato;
    const items = new ItemList();

    // Authenticator name when the AAGUID is recognised, sync-state hint otherwise.
    const sub = (_passkey$authenticato = passkey.authenticatorName()) != null ? _passkey$authenticato : passkey.backupState() ? app.translator.trans('datlechin-passkey.forum.settings.list.synced') : app.translator.trans('datlechin-passkey.forum.settings.list.device_only');
    items.add('title', m("div", {
      className: "PasskeyList-item-title"
    }, m("span", {
      className: "PasskeyList-item-title-main"
    }, passkey.deviceName()), ', ', m("span", {
      className: "PasskeyList-item-title-sub"
    }, sub)), 30);
    items.add('meta', m("div", {
      className: "PasskeyList-item-meta"
    }, this.metaItems(passkey).toArray()), 20);
    return items;
  }
  metaItems(passkey) {
    const items = new ItemList();
    const createdAt = passkey.createdAt();
    if (createdAt) {
      items.add('added', m("span", null, app.translator.trans('datlechin-passkey.forum.settings.list.added_label'), " ", m("strong", null, humanTime(createdAt))), 20);
    }
    const lastUsedAt = passkey.lastUsedAt();
    items.add('lastUsed', m("span", null, lastUsedAt ? m('[', null, app.translator.trans('datlechin-passkey.forum.settings.list.last_used_label'), " ", m("strong", null, humanTime(lastUsedAt))) : app.translator.trans('datlechin-passkey.forum.settings.list.never_used')), 10);
    return items;
  }
  itemActions(passkey) {
    const items = new ItemList();
    items.add('rename', m(Button, {
      className: "Button Button--inverted",
      icon: "fas fa-pen",
      onclick: () => this.openRename(passkey)
    }, app.translator.trans('datlechin-passkey.forum.settings.list.rename_button')), 20);
    items.add('revoke', m(Button, {
      className: "Button Button--danger",
      icon: "fas fa-trash",
      loading: !!this.loading[passkey.id()],
      onclick: () => this.revoke(passkey)
    }, app.translator.trans('datlechin-passkey.forum.settings.list.revoke_button')), 10);
    return items;
  }
  refresh() {
    this.loaded = false;
    m.redraw();
    app.store.find('passkeys').then(passkeys => {
      this.passkeys = passkeys;
      this.loaded = true;
      m.redraw();
    }).catch(() => {
      this.loaded = true;
      m.redraw();
    });
  }
  openAdd() {
    app.modal.show(AddPasskeyModal, {
      onSuccess: () => this.refresh()
    });
  }
  openRename(passkey) {
    app.modal.show(RenamePasskeyModal, {
      passkey,
      onSuccess: () => m.redraw()
    });
  }
  async bulkRevoke() {
    const message = extractText(app.translator.trans('datlechin-passkey.forum.settings.bulk_revoke_modal.body', {
      count: this.passkeys.length
    }));
    if (!confirm(message)) return;
    this.bulkLoading = true;
    m.redraw();
    try {
      await bulkRevoke();
      this.passkeys = [];
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('datlechin-passkey.forum.settings.alerts.bulk_revoke_succeeded'));
    } finally {
      this.bulkLoading = false;
      m.redraw();
    }
  }
  async revoke(passkey) {
    const message = extractText(app.translator.trans('datlechin-passkey.forum.settings.revoke_modal.body', {
      device: passkey.deviceName()
    }));
    if (!confirm(message)) return;
    this.loading[passkey.id()] = true;
    m.redraw();
    try {
      await passkey.delete();
      this.passkeys = this.passkeys.filter(p => p !== passkey);
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('datlechin-passkey.forum.settings.alerts.revoke_succeeded'));
    } finally {
      delete this.loading[passkey.id()];
      m.redraw();
    }
  }
}
flarum.reg.add('datlechin-passkey', 'forum/components/PasskeyList', PasskeyList);